import { useEffect, useRef } from "react";
import { useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

const MapControls = ({
  zoomStep = 1,
  onTogglePanel,
  onClosePanel,           // ⬅️ 추가: 패널 닫기 콜백(선택)
  panelOpen,              // ⬅️ 추가: 현재 패널 열림 여부
  fullscreenTargetRef,
}) => {
  const map = useMap();

  // 최신 콜백/상태를 ref로 보관
  const toggleCbRef = useRef(onTogglePanel);
  const closeCbRef = useRef(onClosePanel);
  const panelOpenRef = useRef(panelOpen);

  useEffect(() => { toggleCbRef.current = onTogglePanel; }, [onTogglePanel]);
  useEffect(() => { closeCbRef.current = onClosePanel; }, [onClosePanel]);
  useEffect(() => { panelOpenRef.current = panelOpen; }, [panelOpen]);

  useEffect(() => {
    const CustomControl = L.Control.extend({
      options: { position: "topleft" },

      onAdd() {
        const container = L.DomUtil.create("div", "leaflet-bar");
        Object.assign(container.style, {
          background: "white",
          border: "1px solid rgba(0,0,0,0.2)",
          borderRadius: "4px",
          overflow: "hidden",
        });

        // const mkBtn = (label, title) => {
        //   const btn = L.DomUtil.create("a", "", container);
        //   btn.href = "#";
        //   btn.title = title;
        //   btn.setAttribute("aria-label", title);
        //   btn.innerHTML = label;
        //   Object.assign(btn.style, {
        //     display: "block", width: "48px", height: "48px",
        //     lineHeight: "48px", textAlign: "center", fontSize: "24px",
        //     textDecoration: "none", userSelect: "none",
        //   });
        //   return btn;
        // };

        const mkImgBtn = (
          imgSrc,
          title,
          { size = 48, imgSize = 24, imgAlt = "", offsetY = 2 } = {} // ⬅️ 아래로 2px 이동
        ) => {
          const btn = L.DomUtil.create("a", "", container);
          btn.href = "#";
          btn.title = title;
          btn.setAttribute("aria-label", title);

          // 버튼을 flex 중앙정렬로
          Object.assign(btn.style, {
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: `${size}px`,
            height: `${size}px`,
            textDecoration: "none",
            userSelect: "none",
          });

          const img = L.DomUtil.create("img", "", btn);
          img.src = imgSrc;
          img.alt = imgAlt || title;
          Object.assign(img.style, {
            width: `${imgSize}px`,
            height: `${imgSize}px`,
            pointerEvents: "none", // 클릭 타깃은 a 요소
            userSelect: "none",
            transform: `translateY(${offsetY}px)`, // ⬅️ 아래로 미세 이동
          });

          img.draggable = false;
          img.addEventListener("dragstart", (e) => e.preventDefault());
          return btn;
        };

        // const zoomInBtn = mkBtn("+", "확대");
        // const zoomOutBtn = mkBtn("–", "축소"); zoomOutBtn.style.borderTop = "1px solid rgba(0,0,0,0.2)";
        // const baseToggleBtn = mkBtn("🗺️", "기본/위성 전환"); baseToggleBtn.style.borderTop = "1px solid rgba(0,0,0,0.2)";
        // const fsBtn = mkBtn("⛶", "전체화면 전환"); fsBtn.style.borderTop = "1px solid rgba(0,0,0,0.2)";
        // // const aBtn = mkBtn("a", "사용자 버튼 A"); aBtn.style.borderTop = "1px solid rgba(0,0,0,0.2)"; aBtn.style.fontSize = "14px";

        // // 전체화면에서만 보이는 패널 버튼
        // const panelBtn = mkBtn("☰", "패널 토글");

        const zoomInBtn = mkImgBtn("/img/geo/plus.png", "지도 확대", { size: 36, imgSize: 20, offsetY: 2 });
        const zoomOutBtn = mkImgBtn("/img/geo/minus.png", "지도 축소", { size: 36, imgSize: 20, offsetY: 2 });

        const baseToggleBtn = mkImgBtn("/img/geo/map.png", "기본/위성 전환", { size: 36, imgSize: 22, offsetY: 2 });
        const fsBtn = mkImgBtn("/img/geo/fullscreen.png", "전체화면 전환", { size: 36, imgSize: 22, offsetY: 1 });
        // const aBtn = mkBtn("a", "사용자 버튼 A"); aBtn.style.borderTop = "1px solid rgba(0,0,0,0.2)"; aBtn.style.fontSize = "14px";

        // 전체화면에서만 보이는 패널 버튼
        const panelBtn = mkImgBtn("/img/geo/navigation.png", "패널 토글", { size: 36, imgSize: 20, offsetY: 8 });

        // panelBtn.style.borderTop = "1px solid rgba(0,0,0,0.2)";
        // panelBtn.style.display = "none";

        const targetEl = fullscreenTargetRef?.current || map.getContainer();

        // 기본/위성 레이어 준비
        let defaultBase = null;
        map.eachLayer((layer) => {
          if (layer instanceof L.TileLayer && !(layer instanceof L.TileLayer.WMS)) {
            if (!defaultBase) defaultBase = layer;
          }
        });
        if (!defaultBase) {
          defaultBase = L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
            attribution: "&copy; OpenStreetMap contributors",
          }).addTo(map);
        }

        const satellite = L.tileLayer(
          "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}",
          { attribution: "Tiles © Esri, Maxar, Earthstar Geographics, GIS User Community" }
        );

        let current = "default";
        const setActiveStyle = () => {
          baseToggleBtn.style.background = current === "satellite" ? "#f0f0f0" : "white";
        };
        setActiveStyle();

        L.DomEvent.disableClickPropagation(container);
        L.DomEvent.disableScrollPropagation(container);

        // 핸들러
        const onZoomIn = (e) => { e.preventDefault(); map.zoomIn(zoomStep); };
        const onZoomOut = (e) => { e.preventDefault(); map.zoomOut(zoomStep); };
        const toggleFullscreen = (e) => {
          e.preventDefault();
          if (document.fullscreenElement) document.exitFullscreen?.();
          else targetEl.requestFullscreen?.();
        };
        // const onClickA = (e) => { e.preventDefault(); alert("반갑습니다."); };
        const toggleBasemap = (e) => {
          e?.preventDefault?.();
          if (current === "default") {
            if (map.hasLayer(defaultBase)) map.removeLayer(defaultBase);
            satellite.addTo(map); current = "satellite";
          } else {
            if (map.hasLayer(satellite)) map.removeLayer(satellite);
            defaultBase.addTo(map); current = "default";
          }
          setActiveStyle();
        };
        const onPanelButtonClick = (e) => {
          e.preventDefault();
          toggleCbRef.current?.();
        };

        // 이벤트 등록
        zoomInBtn.addEventListener("click", onZoomIn);
        zoomOutBtn.addEventListener("click", onZoomOut);
        fsBtn.addEventListener("click", toggleFullscreen);
        // aBtn.addEventListener("click", onClickA);
        baseToggleBtn.addEventListener("click", toggleBasemap);
        panelBtn.addEventListener("click", onPanelButtonClick);

        // 전체화면 상태 변화 감지
        const syncPanelBtnVisibility = () => {
          const isFs = document.fullscreenElement === targetEl;
          panelBtn.style.display = isFs ? "block" : "none";

          // 전체화면 해제 시 패널 닫기
          if (!isFs) {
            if (typeof closeCbRef.current === "function") {
              closeCbRef.current();
            } else if (panelOpenRef.current && typeof toggleCbRef.current === "function") {
              // close 콜백이 없다면 토글로라도 닫아줌
              toggleCbRef.current();
            }
          }

          // 전환 직후 지도 크기 보정
          setTimeout(() => map.invalidateSize(), 0);
        };
        document.addEventListener("fullscreenchange", syncPanelBtnVisibility);
        // 초기 동기화
        syncPanelBtnVisibility();

        // 키보드 단축키
        const isTyping = (el) =>
          el && (el.tagName === "INPUT" || el.tagName === "TEXTAREA" || el.isContentEditable);
        const onKey = (e) => {
          if (isTyping(document.activeElement)) return;
          if (e.key === "=" || e.key === "+") { e.preventDefault(); map.zoomIn(zoomStep); return; }
          if (e.key === "-" || e.key === "_") { e.preventDefault(); map.zoomOut(zoomStep); return; }
          if (e.key === "b" || e.key === "B") { e.preventDefault(); toggleBasemap(); return; }
          // if (e.key === "a" || e.key === "A") { e.preventDefault(); alert("반갑습니다."); return; }
          if (e.key === "p" || e.key === "P") {
            const isFs = document.fullscreenElement === targetEl;
            if (isFs) { e.preventDefault(); toggleCbRef.current?.(); }
          }
        };
        window.addEventListener("keydown", onKey);

        // cleanup
        this._cleanup = () => {
          zoomInBtn.removeEventListener("click", onZoomIn);
          zoomOutBtn.removeEventListener("click", onZoomOut);
          fsBtn.removeEventListener("click", toggleFullscreen);
          // aBtn.removeEventListener("click", onClickA);
          baseToggleBtn.removeEventListener("click", toggleBasemap);
          panelBtn.removeEventListener("click", onPanelButtonClick);
          window.removeEventListener("keydown", onKey);
          document.removeEventListener("fullscreenchange", syncPanelBtnVisibility);
          if (map.hasLayer(satellite)) {
            map.removeLayer(satellite);
            defaultBase.addTo(map);
          }
        };

        return container;
      },

      onRemove() { this._cleanup?.(); },
    });

    const control = new CustomControl();
    map.addControl(control);
    return () => { map.removeControl(control); };
  }, [map, zoomStep, fullscreenTargetRef]);

  return null;
};

export default MapControls;
